import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { UserPlus, Check, X, Clock } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/button';
import PortalLayout from '../../../components/PortalLayout';
import axios from 'axios';

const AccessRequests = () => {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/portal/access-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.requests);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const approveRequest = async (requestId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/portal/access-requests/${requestId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRequests();
    } catch (error) {
      alert('Error approving request');
    }
  };

  const rejectRequest = async (requestId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/portal/access-requests/${requestId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRequests();
    } catch (error) {
      alert('Error rejecting request');
    }
  };

  return (
    <PortalLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Access Requests</h1>
          <p className="text-gray-600">Review and manage portal access requests</p>
        </div>

        <div className="grid gap-4">
          {requests.map((req) => (
            <motion.div key={req.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{req.first_name} {req.last_name}</h3>
                  <p className="text-sm text-gray-600 mb-2">{req.email}</p>
                  {req.company && <p className="text-sm text-gray-500 mb-2">Company: {req.company}</p>}
                  <p className="text-sm text-gray-700 mb-3">Reason: {req.reason}</p>
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span className="flex items-center"><Clock className="w-3 h-3 mr-1" />{new Date(req.created_at).toLocaleDateString()}</span>
                    <span className={`px-2 py-1 rounded-full ${req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : req.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{req.status}</span>
                  </div>
                </div>
                {req.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button onClick={() => approveRequest(req.id)} size="sm" className="bg-green-600 hover:bg-green-700">
                      <Check className="w-4 h-4 mr-1" />Approve
                    </Button>
                    <Button onClick={() => rejectRequest(req.id)} size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                      <X className="w-4 h-4 mr-1" />Reject
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </PortalLayout>
  );
};

export default AccessRequests;
